import React, { useEffect, useMemo } from "react";
import type {
  FieldErrors,
  UseFormRegister,
  UseFormSetValue,
} from "react-hook-form";
import { NativeSelect } from "@picklebet/ui";
import { constructCurrency } from "common/currency";
import { useFiatCurrency } from "@/hooks/firestore/user/useFiatCurrency";
import { useDepositLimits } from "@/hooks/firestore/user/useDepositLimits";
import {
  useIsProfileIomRegulatedMarketPartiallyVerified,
  useIsProfileIomRegulatedMarketUnverified,
} from "@/hooks/firestore/user/useProfile";
import { motion } from "motion/react";
import { Alert } from "@picklebet/ui";
import { Button } from "@picklebet/ui";
import { navigate } from "gatsby";

import * as styles from "./DepositLimits.module.scss";

type DepositLimitsProps = {
  isUpdate?: boolean;
  loading?: boolean;
  setValue: UseFormSetValue<
    any & {
      depositLimit: number;
      depositLimitPeriod: number;
    }
  >;
  register: UseFormRegister<
    any & {
      depositLimit: number;
      depositLimitPeriod: number;
    }
  >;
  errors?: FieldErrors<
    any & {
      depositLimit: number;
      depositLimitPeriod: number;
    }
  >;
  isExcludedPersonsMatch?: boolean;
};

const DEPOSIT_LIMIT_OPTIONS_CENTS = [
  1_000, // $10.00
  2_500, // $25.00
  5_000, // $50.00
  7_500, // $75.00
  10_000, // $100.00
  15_000, // $150.00
  20_000, // $200.00
  25_000, // $250.00
  50_000, // $500.00
  75_000, // $750.00
  100_000, // $1000.00
  200_000, // $2000.00
  500_000, // $5000.00
  1_000_000, // $10,000.00
  2_500_000, // $25,000.00
  5_000_000, // $50,000.00
];

const DAY_MAP: Record<number, string> = {
  1: "Daily",
  7: "Weekly (7 days)",
  14: "Fortnightly (14 days)",
  30: "Monthly (30 days)",
};

const DAY_MAP_KEYS = Object.keys(DAY_MAP).map((value) => Number(value));

const DEFAULT_DEPOSIT_LIMIT_CENTS = 5_000_000;

const DepositLimits = ({
  register,
  errors,
  setValue,
  isUpdate = false,
  loading = false,
  isExcludedPersonsMatch = false,
}: DepositLimitsProps) => {
  const currency = useFiatCurrency();
  const { isProfileIomRegulatedMarketUnverified } =
    useIsProfileIomRegulatedMarketUnverified();
  const { isProfileIomRegulatedMarketPartiallyVerified } =
    useIsProfileIomRegulatedMarketPartiallyVerified();

  const { depositLimit, isLoading: depositLimitsLoading } =
    useDepositLimits(currency);

  const limitPeriodOptions = useMemo(() => {
    // filter options to what's allowed from the backend
    return Object.entries(DAY_MAP).flatMap(([value, label]) =>
      (depositLimit?.rules?.allowedDays || DAY_MAP_KEYS)?.includes(
        Number(value),
      )
        ? [{ label, value }]
        : [],
    );
  }, [depositLimit]);

  const maxDepositAmountCents = depositLimit?.rules?.maximum
    ? depositLimit.rules.maximum
    : DEFAULT_DEPOSIT_LIMIT_CENTS;

  const defaultDepositLimit = useMemo(() => {
    const storedValue = depositLimit?.maximumDailyDepositAmount
      ? depositLimit.maximumDailyDepositAmount
      : null;

    if (!storedValue) {
      return maxDepositAmountCents;
    }

    // if the stored value is higher than what's allowed, bring it back down
    return storedValue > maxDepositAmountCents
      ? maxDepositAmountCents
      : storedValue;
  }, [depositLimit, maxDepositAmountCents]);

  useEffect(() => {
    if (limitPeriodOptions.length === 1) {
      setValue("depositLimitPeriod", limitPeriodOptions[0]["value"]);
    }
  }, [limitPeriodOptions]);

  useEffect(() => {
    // update the form if it changed
    if (defaultDepositLimit) {
      setValue("depositLimit", defaultDepositLimit.toString());
    }
  }, [defaultDepositLimit]);

  const depositLimitOptions = useMemo(() => {
    // options must be less than the max deposit value
    const options = DEPOSIT_LIMIT_OPTIONS_CENTS.filter(
      (val) => val <= maxDepositAmountCents,
    );

    // if the limit isn't an option, add it in
    if (!options.includes(maxDepositAmountCents)) {
      options.push(maxDepositAmountCents);
    }
    // format label as currency, value as string and sort
    return options
      .map((amount) => ({
        label: constructCurrency(amount || 0, {
          currencyType: currency,
          showDecimals: false,
        }),
        value: amount.toString(),
      }))
      .sort((a, b) => Number(a.value) - Number(b.value));
  }, [currency, maxDepositAmountCents]);

  return (
    <>
      <NativeSelect
        placeholder={`${isUpdate ? "Update" : "Set"} your deposit limit`}
        options={depositLimitOptions}
        {...register("depositLimit")}
        errorMessage={errors?.depositLimit?.message as string}
        loading={loading || depositLimitsLoading}
        disabled={isExcludedPersonsMatch}
      />
      <NativeSelect
        placeholder="Limit Period"
        options={limitPeriodOptions}
        {...register("depositLimitPeriod")}
        errorMessage={errors?.depositLimitPeriod?.message as string}
        loading={loading || depositLimitsLoading}
        disabled={
          loading ||
          limitPeriodOptions.length === 1 ||
          isExcludedPersonsMatch ||
          undefined
        }
      />
      {(isProfileIomRegulatedMarketUnverified ||
        isProfileIomRegulatedMarketPartiallyVerified) && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <Alert
            className={styles.alert}
            color={`info`}
            size="large"
            actions={
              <Button
                onClick={() => navigate("/account/verification/")}
                size="sm"
                variant={`info`}
              >
                Verify Now
              </Button>
            }
          >
            {isProfileIomRegulatedMarketPartiallyVerified
              ? "Verify your account to unlock increased deposit and withdrawal limits."
              : "Verify your account to unlock withdrawals and increased deposit limits."}
          </Alert>
        </motion.div>
      )}
    </>
  );
};

DepositLimits.displayName = "DepositLimits";

export { DepositLimits };
